import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckSquare2, Calendar, TrendingUp, AlertCircle } from "lucide-react";
import { useProjects } from "@/contexts/ProjectsContext";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];

export function Statistics() {
  const { projects, loading: projectsLoading } = useProjects();
  const [tasks, setTasks] = useState<TaskWithDetailsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = { current: false };

  useEffect(() => {
    if (projectsLoading || projects.length === 0) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const projectIds = projects.map((p) => p.project_id);

    supabase
      .from("tasks_with_details")
      .select("*")
      .in("project_id", projectIds)
      .then(({ data }) => {
        setTasks(data ?? []);
        hasFetched.current = true;
        setLoading(false);
      });
  }, [projectsLoading, projects]);

  const totalProjects = projects.length;
  const totalTasks = projects.reduce((sum, p) => sum + (p.total_tasks_count || 0), 0);
  const completedTasks = projects.reduce((sum, p) => sum + (p.completed_tasks_count || 0), 0);
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const upcomingTasksCount = tasks.filter((task) => {
    if (!task.due_date || task.status === "done") return false;
    const dueDate = new Date(task.due_date);
    return dueDate >= now && dueDate <= nextWeek;
  }).length;

  const overdueTasksCount = tasks.filter((task) => {
    if (!task.due_date || task.status === "done") return false;
    return new Date(task.due_date) < now;
  }).length;

  if (loading && tasks.length === 0 && !hasFetched.current) {
    return (
      <AppLayout title="Statistics">
        <div className="text-sm text-muted-foreground py-8 text-center">Loading statistics…</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Statistics">
      <div className="w-full space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <CheckSquare2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalProjects}</div>
              <p className="text-xs text-muted-foreground">Active projects</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
              <CheckSquare2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalTasks}</div>
              <p className="text-xs text-muted-foreground">Across all projects</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed Tasks</CardTitle>
              <CheckSquare2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{completedTasks}</div>
              <p className="text-xs text-muted-foreground">{completionRate}% completion rate</p>
              <Progress value={completionRate} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Upcoming Tasks</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{upcomingTasksCount}</div>
              <p className="text-xs text-muted-foreground">Due in next 7 days</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Overdue Tasks
            </CardTitle>
            <CardDescription>Tasks that have passed their due date</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{overdueTasksCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              task{overdueTasksCount !== 1 ? "s" : ""} overdue
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{completionRate}%</div>
              <Progress value={completionRate} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tasks This Week</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{upcomingTasksCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                due in the next 7 days
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
